import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { byEmployeeCost, roleStripeColors, roleStripeKey } from "@/lib/sortEmployees";
import Avatar from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Plus, Pencil, Search, BedDouble, UtensilsCrossed, Trash2, History, X, IdCard } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const empty = {
  name: "", department: "", position: "", monthly_base: 0, overtime_rate: 0, extra_day_rate: 0,
  accommodation: false, meals: false, work_months: [], taf_pct: 1, tribe_pct: 0, comments: "", active: true,
  photo_file_id: null,
};
const MONTHS = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μαϊ", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];

const fmtAudit = (field, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Ναι" : "Όχι";
  if (Array.isArray(v)) return v.length ? v.map((m) => MONTHS[m - 1]).join(", ") : "—";
  if (["monthly_base", "overtime_rate", "extra_day_rate"].includes(field)) return `${Number(v).toLocaleString("el-GR")} €`;
  if (["taf_pct", "tribe_pct"].includes(field)) return `${Math.round(Number(v) * 100)}%`;
  return String(v);
};
const fmtWhen = (iso) => new Date(iso).toLocaleString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function HistoryDialog({ employee, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["emp-history", employee?.id || "all"],
    queryFn: async () => (await api.get(`/employees/history${employee ? `?employee_id=${employee.id}` : ""}`)).data,
  });
  const rows = data?.rows || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="employee-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            Ιστορικό αλλαγών {employee ? `· ${employee.name}` : "· όλοι οι υπάλληλοι"}
          </DialogTitle>
        </DialogHeader>
        {isLoading && <div className="py-6 text-center text-sm text-[#6F6A60]">Φόρτωση…</div>}
        {!isLoading && rows.length === 0 && (
          <div className="py-8 text-center text-sm text-[#6F6A60]">Δεν υπάρχουν καταγεγραμμένες αλλαγές ακόμη.</div>
        )}
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-3 py-2">Πότε</th>
                {!employee && <th className="px-3 py-2">Υπάλληλος</th>}
                <th className="px-3 py-2">Πεδίο</th>
                <th className="px-3 py-2">Από</th>
                <th className="px-3 py-2">Σε</th>
                <th className="px-3 py-2">Ποιος</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`history-row-${r.id}`} className="border-t border-[#E3DED4]">
                  <td className="px-3 py-2 text-xs text-[#6F6A60] tabular whitespace-nowrap">{fmtWhen(r.changed_at)}</td>
                  {!employee && <td className="px-3 py-2 text-xs font-medium text-[#45423C]">{r.employee_name}</td>}
                  <td className="px-3 py-2 text-xs font-medium text-[#45423C]">{r.label}</td>
                  <td className="px-3 py-2 text-xs text-[#991B1B] line-through">{fmtAudit(r.field, r.old_value)}</td>
                  <td className="px-3 py-2 text-xs font-semibold text-[#065F46]">{fmtAudit(r.field, r.new_value)}</td>
                  <td className="px-3 py-2 text-xs text-[#6F6A60]">{r.changed_by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <DialogFooter>
          <button data-testid="history-close" onClick={onClose}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-4 py-2 text-sm font-semibold text-[#6F6A60] hover:border-[#B9AE9A]">
            <X className="h-4 w-4" /> Κλείσιμο
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const companiesOf = (e) => {
  const out = [];
  if ((e.taf_pct ?? 1) > 0) out.push("TAF");
  if ((e.tribe_pct ?? 0) > 0) out.push("TRIBE");
  return out.length ? out : ["TAF"];
};

function EmployeeDialog({ trigger, initial, onSaved }) {
  const { company } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || empty);
  const [photoUp, setPhotoUp] = useState(false);
  const photoRef = useRef();
  const qc = useQueryClient();
  const { data: org } = useQuery({ queryKey: ["org-settings"], queryFn: async () => (await api.get("/settings/org")).data });

  const uploadPhoto = async (f) => {
    if (!f) return;
    setPhotoUp(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, photo_file_id: data.file_id }));
      toast.success("Η φωτογραφία ανέβηκε — πατήστε Αποθήκευση");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPhotoUp(false);
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        monthly_base: Number(form.monthly_base) || 0,
        overtime_rate: Number(form.overtime_rate) || 0,
        extra_day_rate: Number(form.extra_day_rate) || 0,
        taf_pct: Number(form.taf_pct) || 0,
        tribe_pct: Number(form.tribe_pct) || 0,
        accommodation: !!form.accommodation,
        meals: !!form.meals,
        work_months: (form.work_months || []).map(Number),
      };
      if (initial?.id) return (await api.put(`/employees/${initial.id}`, payload)).data;
      return (await api.post("/employees", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      toast.success(initial?.id ? "Ο υπάλληλος ενημερώθηκε" : "Ο υπάλληλος προστέθηκε");
      setOpen(false);
      onSaved?.();
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const field = (k) => ({ value: form[k] ?? "", onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  const toggleMonth = (m) => {
    const cur = form.work_months || [];
    setForm({ ...form, work_months: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m].sort((a, b) => a - b) });
  };
  const inp = "mt-1 w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";
  const lab = "text-xs uppercase tracking-wider text-[#6F6A60] font-semibold";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(initial || empty); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{initial?.id ? "Επεξεργασία Υπαλλήλου" : "Νέος Υπάλληλος"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 flex items-center gap-4">
            <Avatar name={form.name} fileId={form.photo_file_id} size="lg" testid="emp-photo-preview" />
            <div>
              <label className={lab}>Φωτογραφία προφίλ</label>
              <input ref={photoRef} type="file" accept="image/*" className="hidden" data-testid="emp-photo-input"
                onChange={(e) => uploadPhoto(e.target.files[0])} />
              <div className="mt-1 flex gap-2">
                <button type="button" data-testid="emp-photo-btn" onClick={() => photoRef.current?.click()}
                  className="rounded-lg border border-[#E3DED4] px-3 py-1.5 text-xs font-semibold text-[#45423C] hover:border-[#B9AE9A]">
                  {photoUp ? "Ανέβασμα…" : form.photo_file_id ? "Αλλαγή φωτογραφίας" : "Ανέβασμα φωτογραφίας"}
                </button>
                {form.photo_file_id && (
                  <button type="button" data-testid="emp-photo-remove" onClick={() => setForm({ ...form, photo_file_id: null })}
                    className="rounded-lg border border-[#E3DED4] px-3 py-1.5 text-xs font-semibold text-[#991B1B] hover:border-[#991B1B]">
                    Αφαίρεση
                  </button>
                )}
              </div>
              <p className="text-[10px] text-[#8A8577] mt-1">Εμφανίζεται στον κατάλογο και στο πρόγραμμα βαρδιών.</p>
            </div>
          </div>
          <div className="col-span-2">
            <label className={lab}>Ονοματεπώνυμο</label>
            <input data-testid="emp-name" className={inp} {...field("name")} />
          </div>
          <div>
            <label className={lab}>Ρόλος / Θέση</label>
            <select data-testid="emp-position" className={inp} {...field("position")}>
              <option value="">— επιλέξτε —</option>
              {(org?.positions || []).map((p) => <option key={p} value={p}>{p}</option>)}
              {form.position && !(org?.positions || []).includes(form.position) && <option value={form.position}>{form.position}</option>}
            </select>
          </div>
          <div>
            <label className={lab}>Τμήμα</label>
            <select data-testid="emp-dept" className={inp} {...field("department")}>
              <option value="">— επιλέξτε —</option>
              {(org?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
              {form.department && !(org?.departments || []).includes(form.department) && <option value={form.department}>{form.department}</option>}
            </select>
          </div>
          <div>
            <label className={lab}>Βασικός Μισθός (€)</label>
            <input data-testid="emp-base" type="number" className={inp} {...field("monthly_base")} />
          </div>
          <div>
            <label className={lab}>Υπερωρία (€)</label>
            <input data-testid="emp-ot-rate" type="number" step="0.5" className={inp} {...field("overtime_rate")} />
          </div>
          <div>
            <label className={lab}>Υπερημέρα (€)</label>
            <input data-testid="emp-extraday" type="number" step="0.5" className={inp} {...field("extra_day_rate")} />
          </div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-2 text-sm text-[#45423C]">
              <input data-testid="emp-accommodation" type="checkbox" checked={!!form.accommodation}
                onChange={(e) => setForm({ ...form, accommodation: e.target.checked })} />
              Διαμονή
            </label>
            <label className="flex items-center gap-2 text-sm text-[#45423C]">
              <input data-testid="emp-meals" type="checkbox" checked={!!form.meals}
                onChange={(e) => setForm({ ...form, meals: e.target.checked })} />
              Σίτιση
            </label>
          </div>
          {company === "taf" && (
            <>
              <div>
                <label className={lab}>Μερίδιο TAF (%)</label>
                <input data-testid="emp-taf" type="number" step="1" min="0" max="100" className={inp}
                  value={Math.round((Number(form.taf_pct) || 0) * 100)}
                  onChange={(e) => setForm({ ...form, taf_pct: (Number(e.target.value) || 0) / 100 })} />
              </div>
              <div>
                <label className={lab}>Μερίδιο TRIBE (%)</label>
                <input data-testid="emp-tribe" type="number" step="1" min="0" max="100" className={inp}
                  value={Math.round((Number(form.tribe_pct) || 0) * 100)}
                  onChange={(e) => setForm({ ...form, tribe_pct: (Number(e.target.value) || 0) / 100 })} />
              </div>
            </>
          )}
          <div className="col-span-2">
            <label className={lab}>Μήνες εργασίας ({(form.work_months || []).length}/12)</label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {MONTHS.map((m, i) => {
                const on = (form.work_months || []).includes(i + 1);
                return (
                  <button key={m} type="button" data-testid={`emp-month-${i + 1}`} onClick={() => toggleMonth(i + 1)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${on ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#6F6A60] border-[#E3DED4] hover:border-[#B9AE9A]"}`}>
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-span-2">
            <label className={lab}>Σχόλια</label>
            <input data-testid="emp-comments" className={inp} {...field("comments")} />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="emp-save" onClick={() => save.mutate()} disabled={save.isPending || !form.name}
            className="rounded-xl bg-[#45423C] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
            {save.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Chip = ({ active, children, ...rest }) => (
  <button {...rest} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#6F6A60] border-[#E3DED4] hover:border-[#B9AE9A]"}`}>
    {children}
  </button>
);

const cellBtn = "rounded-md px-1.5 py-1 -mx-1.5 hover:bg-[#F1EDE6] hover:ring-1 hover:ring-[#E3DED4] transition-colors";

function InlineText({ value, onSave, testid, align = "left", format, type = "text", disabled }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  if (disabled) return <span className={align === "right" ? "tabular" : ""}>{format ? format(value) : (value || "—")}</span>;
  if (!editing) {
    return (
      <button data-testid={testid} title="Κλικ για αλλαγή"
        onClick={() => { setVal(value ?? ""); setEditing(true); }}
        className={`${cellBtn} ${align === "right" ? "tabular" : ""}`}>
        {format ? format(value) : (value || "—")}
      </button>
    );
  }
  const commit = () => {
    setEditing(false);
    const next = type === "number" ? Number(val) || 0 : String(val).trim();
    if (next !== value && !(type === "text" && !next)) onSave(next);
  };
  return (
    <input data-testid={`${testid}-input`} autoFocus type={type} step={type === "number" ? "0.01" : undefined}
      value={val} onChange={(ev) => setVal(ev.target.value)} onBlur={commit}
      onKeyDown={(ev) => { if (ev.key === "Enter") commit(); if (ev.key === "Escape") setEditing(false); }}
      className={`w-full min-w-[90px] rounded-md border border-[#45423C] px-1.5 py-1 text-sm outline-none ${align === "right" ? "text-right tabular" : ""}`} />
  );
}

function InlineSelect({ value, options, onSave, testid, render, disabled }) {
  const [editing, setEditing] = useState(false);
  if (disabled) return render ? render(value) : <span>{value || "—"}</span>;
  if (!editing) {
    return (
      <button data-testid={testid} onClick={() => setEditing(true)} className={cellBtn} title="Κλικ για αλλαγή">
        {render ? render(value) : (value || "—")}
      </button>
    );
  }
  return (
    <select data-testid={`${testid}-select`} autoFocus value={value || ""} onBlur={() => setEditing(false)}
      onChange={(ev) => { setEditing(false); if (ev.target.value !== value) onSave(ev.target.value); }}
      className="w-full min-w-[110px] rounded-md border border-[#45423C] px-1 py-1 text-sm outline-none">
      <option value="">—</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}


export default function Employees() {
  const { user, company } = useAuth();
  const [q, setQ] = useState("");
  const [roles, setRoles] = useState([]);
  const [depts, setDepts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [historyFor, setHistoryFor] = useState(undefined);
  const canEdit = ["owner", "hr"].includes(user?.role);
  const { data = [], isLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
  });

  const qc = useQueryClient();
  const { data: org } = useQuery({ queryKey: ["org-settings"], queryFn: async () => (await api.get("/settings/org")).data });
  const patchEmp = useMutation({
    mutationFn: async ({ id, patch }) => (await api.patch(`/employees/${id}`, patch)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["schedule-week"] });
      qc.invalidateQueries({ queryKey: ["emp-history"] });
      toast.success(`Αποθηκεύτηκε: ${d.name}`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const setField = (e, field) => (v) => patchEmp.mutate({ id: e.id, patch: { [field]: v } });
  const toggleMonth = (e, m) => {
    const months = e.work_months || [];
    const next = months.includes(m) ? months.filter((x) => x !== m) : [...months, m].sort((a, b) => a - b);
    patchEmp.mutate({ id: e.id, patch: { work_months: next } });
  };
  const removeEmp = useMutation({
    mutationFn: async ({ id, permanent }) => (await api.delete(`/employees/${id}${permanent ? "?permanent=true" : ""}`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["trash"] });
      toast.success(d.status === "erased" ? `Ο/Η ${d.name} διαγράφηκε οριστικά` : `Ο/Η ${d.name} μεταφέρθηκε στον Κάδο Ανακύκλωσης`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const askRemove = (e) => {
    if (!window.confirm(`Διαγραφή του υπαλλήλου «${e.name}»;\n\nΦεύγει από τον κατάλογο και το πρόγραμμα, αλλά παραμένει στις παλιές μισθοδοσίες και στα στατιστικά.`)) return;
    const permanent = user?.role === "owner" && window.confirm("ΟΡΙΣΤΙΚΗ διαγραφή;\n\nOK = σβήνεται εντελώς (κρατείται μόνο backup) · Άκυρο = πάει στον Κάδο Ανακύκλωσης και μπορεί να επανέλθει");
    removeEmp.mutate({ id: e.id, permanent });
  };

  const allRoles = Array.from(new Set(data.map((e) => e.position || e.department).filter(Boolean))).sort();
  const allDepts = Array.from(new Set(data.map((e) => e.department).filter(Boolean))).sort();
  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const empCost = (e) => (e.monthly_base || 0) * ((e.work_months || []).length || 12);
  const rows = data.filter((e) => {
    const matchQ = e.name.toLowerCase().includes(q.toLowerCase()) || (e.department || "").toLowerCase().includes(q.toLowerCase());
    const matchRole = roles.length === 0 || roles.includes(e.position || e.department);
    const matchDept = depts.length === 0 || depts.includes(e.department);
    const matchCo = companies.length === 0 || companiesOf(e).some((c) => companies.includes(c));
    return matchQ && matchRole && matchDept && matchCo;
  }).sort(byEmployeeCost(empCost));
  const empDeptColors = roleStripeColors(rows);

  const totals = rows.reduce((a, e) => ({
    base: a.base + (e.monthly_base || 0),
    ot: a.ot + (e.overtime_rate || 0),
    xd: a.xd + (e.extra_day_rate || 0),
    acc: a.acc + (e.accommodation ? 1 : 0),
    meals: a.meals + (e.meals ? 1 : 0),
    months: a.months + (e.work_months || []).length,
    taf: a.taf + (e.taf_pct || 0) * 100,
    tribe: a.tribe + (e.tribe_pct || 0) * 100,
  }), { base: 0, ot: 0, xd: 0, acc: 0, meals: 0, months: 0, taf: 0, tribe: 0 });

  return (
    <div className="space-y-5" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Κατάλογος</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Υπάλληλοι</h1>
          {canEdit && <p className="text-[#6F6A60] text-sm mt-1">Κλικ σε οποιοδήποτε κελί για άμεση αλλαγή — όνομα, ρόλο, τμήμα, μισθό, μήνες εργασίας.</p>}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button data-testid="open-all-history" onClick={() => setHistoryFor(null)}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A]">
              <History className="h-4 w-4" /> Ιστορικό αλλαγών
            </button>
            <EmployeeDialog
              trigger={
                <button data-testid="add-employee-btn" className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A]">
                  <Plus className="h-4 w-4" /> Προσθήκη Υπαλλήλου
                </button>
              }
            />
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 space-y-3" data-testid="employee-filters">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6F6A60]" />
            <input data-testid="employee-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Αναζήτηση ονόματος ή τμήματος…"
              className="w-full rounded-xl border border-[#E3DED4] bg-white pl-10 pr-4 py-2 text-sm outline-none focus:border-[#45423C]" />
          </div>
          <span className="text-xs text-[#6F6A60]">{rows.length} από {data.length}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold mr-1">Ρόλος</span>
          <Chip data-testid="role-chip-all" active={roles.length === 0} onClick={() => setRoles([])}>Όλοι</Chip>
          {allRoles.map((r) => (
            <Chip key={r} data-testid={`role-chip-${r}`} active={roles.includes(r)} onClick={() => toggle(roles, setRoles, r)}>{r}</Chip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="dept-filters">
          <span className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold mr-1">Τμήμα</span>
          <Chip data-testid="dept-chip-all" active={depts.length === 0} onClick={() => setDepts([])}>Όλα</Chip>
          {allDepts.map((d) => (
            <Chip key={d} data-testid={`dept-chip-${d}`} active={depts.includes(d)} onClick={() => toggle(depts, setDepts, d)}>{d}</Chip>
          ))}
        </div>
        {company === "taf" && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold mr-1">Εταιρεία</span>
          <Chip data-testid="co-chip-all" active={companies.length === 0} onClick={() => setCompanies([])}>Όλες</Chip>
          {["TAF", "TRIBE"].map((c) => (
            <Chip key={c} data-testid={`co-chip-${c}`} active={companies.includes(c)} onClick={() => toggle(companies, setCompanies, c)}>{c}</Chip>
          ))}
        </div>
        )}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-3 py-2.5 font-semibold">#</th>
                <th className="px-3 py-2.5 font-semibold">Όνομα</th>
                <th className="px-3 py-2.5 font-semibold">Ρόλος / Θέση</th>
                <th className="px-3 py-2.5 font-semibold">Τμήμα</th>
                <th className="px-3 py-2.5 font-semibold text-right">Βασικός</th>
                <th className="px-3 py-2.5 font-semibold text-right">Υπερωρία</th>
                <th className="px-3 py-2.5 font-semibold text-right">Υπερημέρα</th>
                <th className="px-3 py-2.5 font-semibold text-center">Διαμονή</th>
                <th className="px-3 py-2.5 font-semibold text-center">Σίτιση</th>
                <th className="px-3 py-2.5 font-semibold">Μήνες</th>
                {company === "taf" && <th className="px-3 py-2.5 font-semibold text-center">TAF / TRIBE</th>}
                {canEdit && <th className="px-3 py-2.5" />}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={11} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {rows.map((e) => (
                <tr key={e.id} data-testid={`employee-row-${e.code}`} className={`border-t border-[#E3DED4] hover:bg-[#F7F5F1] ${empDeptColors[roleStripeKey(e)] === 1 ? "bg-[#F4F1EA]" : ""}`}>
                  <td className="px-3 py-2 font-mono text-[#6F6A60]">{e.code}</td>
                  <td className="px-3 py-2 font-medium text-[#45423C]">
                    <span className="inline-flex items-center gap-2">
                      <Avatar name={e.name} fileId={e.photo_file_id} size="sm" testid={`emp-avatar-${e.code}`} />
                      <InlineText value={e.name} disabled={!canEdit} testid={`inline-name-${e.code}`}
                        onSave={setField(e, "name")} />
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <InlineSelect value={e.position || ""} disabled={!canEdit} testid={`inline-position-${e.code}`}
                      options={org?.positions || allRoles} onSave={setField(e, "position")}
                      render={(v) => (
                        <span className="px-2 py-0.5 rounded-full bg-[#EEF0EA] text-[#6E7A63] text-xs font-medium">
                          {v || e.department || "—"}
                        </span>
                      )} />
                  </td>
                  <td className="px-3 py-2 text-[#6F6A60] text-xs">
                    <InlineSelect value={e.department || ""} disabled={!canEdit} testid={`inline-dept-${e.code}`}
                      options={org?.departments || allDepts} onSave={setField(e, "department")} />
                  </td>
                  <td className="px-3 py-2 text-right tabular font-medium">
                    <InlineText value={e.monthly_base || 0} type="number" align="right" disabled={!canEdit}
                      testid={`inline-base-${e.code}`} format={fmtEuro} onSave={setField(e, "monthly_base")} />
                  </td>
                  <td className="px-3 py-2 text-right tabular" data-testid={`emp-ot-${e.code}`}>
                    <InlineText value={e.overtime_rate || 0} type="number" align="right" disabled={!canEdit}
                      testid={`inline-ot-${e.code}`} format={(v) => (v ? fmtEuro(v) : "—")} onSave={setField(e, "overtime_rate")} />
                  </td>
                  <td className="px-3 py-2 text-right tabular" data-testid={`emp-xd-${e.code}`}>
                    <InlineText value={e.extra_day_rate || 0} type="number" align="right" disabled={!canEdit}
                      testid={`inline-xd-${e.code}`} format={(v) => (v ? fmtEuro(v) : "—")} onSave={setField(e, "extra_day_rate")} />
                  </td>
                  <td className="px-3 py-2 text-center" data-testid={`emp-acc-${e.code}`}>
                    <button data-testid={`inline-acc-${e.code}`} disabled={!canEdit} title="Διαμονή"
                      onClick={() => setField(e, "accommodation")(!e.accommodation)}
                      className={`p-1 rounded-md ${canEdit ? "hover:bg-[#F1EDE6]" : ""}`}>
                      {e.accommodation ? <BedDouble className="h-4 w-4 mx-auto text-[#6E7A63]" /> : <span className="text-[#6F6A60]">—</span>}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-center" data-testid={`emp-meal-${e.code}`}>
                    <button data-testid={`inline-meal-${e.code}`} disabled={!canEdit} title="Σίτιση"
                      onClick={() => setField(e, "meals")(!e.meals)}
                      className={`p-1 rounded-md ${canEdit ? "hover:bg-[#F1EDE6]" : ""}`}>
                      {e.meals ? <UtensilsCrossed className="h-4 w-4 mx-auto text-[#6E7A63]" /> : <span className="text-[#6F6A60]">—</span>}
                    </button>
                  </td>
                  <td className="px-3 py-2" data-testid={`emp-months-${e.code}`}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-[#45423C] tabular w-3">{(e.work_months || []).length || "—"}</span>
                      <span className="flex gap-0.5">
                        {MONTHS.map((m, i) => {
                          const on = (e.work_months || []).includes(i + 1);
                          return (
                            <button key={m} title={`${m} — κλικ για εναλλαγή`} disabled={!canEdit}
                              data-testid={`inline-month-${e.code}-${i + 1}`}
                              onClick={() => toggleMonth(e, i + 1)}
                              className={`h-4 w-2.5 rounded-[2px] transition-colors ${on ? "bg-[#B9AE9A]" : "bg-[#E3DED4]"} ${canEdit ? "hover:ring-1 hover:ring-[#45423C]" : ""}`} />
                          );
                        })}
                      </span>
                    </span>
                  </td>
                  {company === "taf" && (
                  <td className="px-3 py-2 text-center text-xs text-[#6F6A60] tabular">
                    <InlineText value={Math.round((e.taf_pct || 0) * 100)} type="number" disabled={!canEdit}
                      testid={`inline-taf-${e.code}`} format={(v) => `${v}% / ${Math.round((e.tribe_pct || 0) * 100)}%`}
                      onSave={(v) => patchEmp.mutate({ id: e.id,
                        patch: { taf_pct: Math.min(Math.max(v, 0), 100) / 100, tribe_pct: (100 - Math.min(Math.max(v, 0), 100)) / 100 } })} />
                  </td>
                  )}
                  {canEdit && (
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                      <Link to={`/team/settings?tab=team&member=${e.member_id || e.id}`} data-testid={`card-employee-${e.code}`} title="Πλήρης καρτέλα (Team + Μισθοδοσία)" className="p-1.5 rounded-lg hover:bg-[#F1EDE6] text-[#C86235]">
                        <IdCard className="h-4 w-4" />
                      </Link>
                      <EmployeeDialog
                        initial={e}
                        trigger={
                          <button data-testid={`edit-employee-${e.code}`} className="p-1.5 rounded-lg hover:bg-[#F1EDE6] text-[#6F6A60]">
                            <Pencil className="h-4 w-4" />
                          </button>
                        }
                      />
                      <button data-testid={`history-employee-${e.code}`} title="Ιστορικό αλλαγών"
                        onClick={() => setHistoryFor(e)}
                        className="p-1.5 rounded-lg hover:bg-[#F1EDE6] text-[#6F6A60]">
                        <History className="h-4 w-4" />
                      </button>
                      <button data-testid={`delete-employee-${e.code}`} title="Διαγραφή"
                        onClick={() => askRemove(e)}
                        className="p-1.5 rounded-lg hover:bg-[#FEF2F2] text-[#991B1B]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-[#6F6A60]">Δεν βρέθηκαν υπάλληλοι.</td></tr>
              )}
            </tbody>
            {!isLoading && rows.length > 0 && (
              <tfoot>
                <tr className="bg-[#45423C] text-white text-[13px] font-semibold" data-testid="employees-totals">
                  <td className="px-3 py-2.5" colSpan={4}>ΣΥΝΟΛΟ {rows.length} υπαλλήλων</td>
                  <td className="px-3 py-2.5 text-right tabular" data-testid="emp-total-base">{fmtEuro(totals.base)}</td>
                  <td className="px-3 py-2.5 text-right tabular" data-testid="emp-total-ot">{fmtEuro(totals.ot)}</td>
                  <td className="px-3 py-2.5 text-right tabular" data-testid="emp-total-xd">{fmtEuro(totals.xd)}</td>
                  <td className="px-3 py-2.5 text-center tabular" data-testid="emp-total-acc">{totals.acc} ✓</td>
                  <td className="px-3 py-2.5 text-center tabular" data-testid="emp-total-meals">{totals.meals} ✓</td>
                  <td className="px-3 py-2.5 tabular text-xs">{totals.months} μηνο-θέσεις</td>
                  <td className="px-3 py-2.5 text-center tabular text-xs">
                    {rows.length ? `${Math.round(totals.taf / rows.length)}% / ${Math.round(totals.tribe / rows.length)}%` : "—"}
                  </td>
                  {canEdit && <td className="px-3 py-2.5" />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {historyFor !== undefined && (
        <HistoryDialog employee={historyFor} onClose={() => setHistoryFor(undefined)} />
      )}
    </div>
  );
}
